/* src/pages/Chat.jsx
 * Live Chat Widget – embed code, setup, save configs, try-it-here test
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useIsMobile } from '../hooks/useIsMobile';
import TrackingConfig from '../components/TrackingConfig';

import { CHAT_API_URL } from '../utils/api';
const WIDGET_URL = `${window.location.origin}/widget.js`;
const DEFAULT_ACCENT = '#C8FF4D';
const THEME_OPTIONS = ['light', 'dark', 'auto'];
const DEFAULT_THEME = 'light';

function normalizeHex(value) {
  var v = (value || '').trim();
  if (!v) return DEFAULT_ACCENT;
  if (v.charAt(0) !== '#') v = '#' + v;
  if (/^#[0-9A-Fa-f]{3}$/.test(v)) {
    return '#' + v.charAt(1) + v.charAt(1) + v.charAt(2) + v.charAt(2) + v.charAt(3) + v.charAt(3);
  }
  if (/^#[0-9A-Fa-f]{6}$/.test(v)) return v.toUpperCase();
  return DEFAULT_ACCENT;
}

function normalizeTheme(value) {
  const v = (value || DEFAULT_THEME).toLowerCase();
  return THEME_OPTIONS.includes(v) ? v : DEFAULT_THEME;
}

export default function Chat() {
  const navigate = useNavigate();
  const { selectedWorkspaceId } = useWorkspace();
  const isMobile = useIsMobile();
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [selectedKbId, setSelectedKbId] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [ctaText, setCtaText] = useState('Schedule a call');
  const [ctaPhone, setCtaPhone] = useState('');
  const [ctaEmail, setCtaEmail] = useState('');
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState('');
  const [configLabel, setConfigLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [chatContent, setChatContent] = useState('');
  const [savingChatContent, setSavingChatContent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tryIt, setTryIt] = useState(false);
  const tryItRef = useRef(false);
  const [configTrackingPixels, setConfigTrackingPixels] = useState([]);
  const [accentColor, setAccentColor] = useState(DEFAULT_ACCENT);
  const [themeMode, setThemeMode] = useState(DEFAULT_THEME);

  useEffect(() => {
    if (selectedWorkspaceId) {
      setSelectedKbId('');
      setSelectedConfigId('');
      setConfigLabel('');
      loadKnowledgeBases();
      loadSavedConfigs();
    } else {
      setLoading(false);
    }
  }, [selectedWorkspaceId]);

  useEffect(() => {
    if (selectedKbId && knowledgeBases.length > 0) {
      const kb = knowledgeBases.find((k) => k.id === selectedKbId);
      setChatContent(kb?.chatContent || '');
    } else {
      setChatContent('');
    }
  }, [selectedKbId, knowledgeBases]);

  async function loadKnowledgeBases() {
    if (!selectedWorkspaceId) return;
    try {
      const [kbSnap, formsSnap] = await Promise.all([
        getDocs(query(collection(db, 'knowledge_bases'), where('workspaceId', '==', selectedWorkspaceId))),
        getDocs(query(collection(db, 'forms'), where('workspaceId', '==', selectedWorkspaceId))),
      ]);
      const kbList = [];
      kbSnap.forEach((d) => kbList.push({ id: d.id, ...d.data() }));
      const formByKb = {};
      formsSnap.forEach((d) => {
        const fd = d.data();
        const kbId = fd.settings?.knowledgeBaseId;
        if (kbId) formByKb[kbId] = { name: fd.name || 'Unnamed Form', createdAt: fd.createdAt };
      });
      const list = kbList.map((kb) => {
        const formInfo = formByKb[kb.id] || null;
        return {
          ...kb,
          formName: formInfo?.name || null,
          formCreatedAt: formInfo?.createdAt || null,
        };
      });

      // Pass 1: append short date to any base labels that collide
      const baseLabelCount = {};
      list.forEach((kb) => {
        const base = kb.formName || kb.name || kb.id;
        baseLabelCount[base] = (baseLabelCount[base] || 0) + 1;
      });
      const pass1 = list.map((kb) => {
        const base = kb.formName || kb.name || kb.id;
        if (baseLabelCount[base] > 1) {
          const dateVal = kb.formCreatedAt || kb.createdAt;
          if (dateVal) {
            const d = dateVal.toDate ? dateVal.toDate() : new Date(dateVal);
            const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            return { ...kb, _candidate: `${base} · ${dateStr}` };
          }
          return { ...kb, _candidate: `${base} · ${kb.id.slice(0, 4)}` };
        }
        return { ...kb, _candidate: base };
      });

      // Pass 2: append id slice to any candidate labels still duplicated
      const candidateCount = {};
      pass1.forEach((kb) => {
        candidateCount[kb._candidate] = (candidateCount[kb._candidate] || 0) + 1;
      });
      const disambiguated = pass1.map((kb) => {
        const label =
          candidateCount[kb._candidate] > 1
            ? `${kb._candidate} · ${kb.id.slice(0, 4)}`
            : kb._candidate;
        const { _candidate, ...rest } = kb;
        return { ...rest, displayLabel: label };
      });

      setKnowledgeBases(disambiguated);
      if (disambiguated.length > 0 && !selectedKbId) setSelectedKbId(disambiguated[0].id);
    } catch (e) {
      console.error('Error loading knowledge bases:', e);
    } finally {
      setLoading(false);
    }
  }

  async function loadSavedConfigs() {
    if (!selectedWorkspaceId) return;
    try {
      const snap = await getDocs(query(collection(db, 'chat_configs'), where('workspaceId', '==', selectedWorkspaceId)));
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setSavedConfigs(list);
      setSelectedConfigId((prev) => (list.some((c) => c.id === prev) ? prev : ''));
    } catch (e) {
      console.error('Error loading chat configs:', e);
    }
  }

  function loadConfig(config) {
    setSelectedConfigId(config.id);
    setConfigLabel(config.label || '');
    const kbId = config.knowledgeBaseId || '';
    const exists = knowledgeBases.some((kb) => kb.id === kbId);
    setSelectedKbId(exists ? kbId : (knowledgeBases[0]?.id || ''));
    setCtaUrl(config.ctaUrl || '');
    setCtaText(config.ctaText || 'Schedule a call');
    setCtaPhone(config.ctaPhone || '');
    setCtaEmail(config.ctaEmail || '');
    setConfigTrackingPixels(config.trackingPixels || []);
    setThemeMode(normalizeTheme(config.themeMode));
  }

  async function saveConfig() {
    if (!selectedWorkspaceId || !selectedKbId) return;
    const label = (configLabel || '').trim() || 'Chat widget';
    setSaving(true);
    try {
      const data = {
        workspaceId: selectedWorkspaceId,
        label,
        knowledgeBaseId: selectedKbId,
        ctaUrl: (ctaUrl || '').trim(),
        ctaText: (ctaText || '').trim() || 'Schedule a call',
        ctaPhone: (ctaPhone || '').trim(),
        ctaEmail: (ctaEmail || '').trim(),
        trackingPixels: configTrackingPixels || [],
        themeMode: normalizeTheme(themeMode),
        updatedAt: new Date(),
      };
      if (selectedConfigId) {
        const { workspaceId: _workspaceId, ...updateData } = data;
        await updateDoc(doc(db, 'chat_configs', selectedConfigId), updateData);
      } else {
        data.createdAt = new Date();
        const ref = await addDoc(collection(db, 'chat_configs'), data);
        setSelectedConfigId(ref.id);
      }
      await loadSavedConfigs();
    } catch (e) {
      console.error('Error saving chat config:', e);
    } finally {
      setSaving(false);
    }
  }

  async function deleteConfig(id) {
    try {
      await deleteDoc(doc(db, 'chat_configs', id));
      if (selectedConfigId === id) {
        setSelectedConfigId('');
        setConfigLabel('');
        setSelectedKbId(knowledgeBases[0]?.id || '');
        setCtaUrl('');
        setCtaText('Schedule a call');
        setCtaPhone('');
        setCtaEmail('');
        setConfigTrackingPixels([]);
        setThemeMode(DEFAULT_THEME);
      }
      await loadSavedConfigs();
    } catch (e) {
      console.error('Error deleting chat config:', e);
    }
  }

  async function saveChatContent() {
    if (!selectedKbId) return;
    setSavingChatContent(true);
    try {
      await updateDoc(doc(db, 'knowledge_bases', selectedKbId), {
        chatContent: (chatContent || '').trim(),
        updatedAt: new Date(),
      });
      await loadKnowledgeBases();
    } catch (e) {
      console.error('Error saving chat content:', e);
    } finally {
      setSavingChatContent(false);
    }
  }

  function clearForm() {
    setSelectedConfigId('');
    setConfigLabel('');
    setSelectedKbId(knowledgeBases[0]?.id || '');
    setCtaUrl('');
    setCtaText('Schedule a call');
    setCtaPhone('');
    setCtaEmail('');
    setConfigTrackingPixels([]);
    setThemeMode(DEFAULT_THEME);
  }

  const embedParts = [
    `src="${WIDGET_URL}"`,
    `data-workspace="${selectedWorkspaceId}"`,
    `data-kb="${selectedKbId}"`,
    `data-api="${CHAT_API_URL}"`,
  ];
  if (selectedConfigId) embedParts.push(`data-config="${selectedConfigId}"`);
  if (ctaUrl && ctaUrl.trim()) {
    embedParts.push(`data-cta-url="${ctaUrl.trim()}"`);
    if (ctaText && ctaText.trim()) embedParts.push(`data-cta-text="${ctaText.trim().replace(/"/g, '&quot;')}"`);
  }
  if (ctaPhone && ctaPhone.trim()) embedParts.push(`data-cta-phone="${ctaPhone.trim().replace(/"/g, '&quot;')}"`);
  if (ctaEmail && ctaEmail.trim()) embedParts.push(`data-cta-email="${ctaEmail.trim().replace(/"/g, '&quot;')}"`);
  if (accentColor && accentColor.toUpperCase() !== DEFAULT_ACCENT) {
    embedParts.push(`data-accent="${accentColor}"`);
  }
  if (themeMode !== 'light') {
    embedParts.push(`data-theme="${themeMode}"`);
  }
  const embedCode = selectedWorkspaceId && selectedKbId
    ? `<script\n  ${embedParts.join('\n  ')}\n></script>`
    : '';

  useEffect(() => {
    if (!tryIt || tryItRef.current || !selectedWorkspaceId || !selectedKbId) return;
    tryItRef.current = true;
    const script = document.createElement('script');
    script.src = WIDGET_URL + '?v=' + Date.now();
    script.setAttribute('data-workspace', selectedWorkspaceId);
    script.setAttribute('data-kb', selectedKbId);
    script.setAttribute('data-api', CHAT_API_URL);
    if (ctaUrl && ctaUrl.trim()) {
      script.setAttribute('data-cta-url', ctaUrl.trim());
      if (ctaText && ctaText.trim()) script.setAttribute('data-cta-text', ctaText.trim());
    }
    if (ctaPhone && ctaPhone.trim()) script.setAttribute('data-cta-phone', ctaPhone.trim());
    if (ctaEmail && ctaEmail.trim()) script.setAttribute('data-cta-email', ctaEmail.trim());
    if (selectedConfigId) script.setAttribute('data-config', selectedConfigId);
    if (accentColor && accentColor.toUpperCase() !== DEFAULT_ACCENT) {
      script.setAttribute('data-accent', accentColor);
    }
    if (themeMode !== 'light') {
      script.setAttribute('data-theme', themeMode);
    }
    script.setAttribute('data-fresh', 'true');
    script.onload = () => setTryIt(true);
    script.onerror = () => {
      tryItRef.current = false;
      setTryIt(false);
    };
    document.body.appendChild(script);
    return () => {
      const root = document.getElementById('smartforms-chat-root');
      if (root) root.remove();
      tryItRef.current = false;
    };
  }, [tryIt, selectedWorkspaceId, selectedKbId, selectedConfigId, ctaUrl, ctaText, ctaPhone, ctaEmail, accentColor, themeMode]);

  const pagePad = isMobile ? '1rem 1rem 2rem' : '24px 32px 48px';
  const selectedKb = knowledgeBases.find((k) => k.id === selectedKbId);
  const selectedKbMissingForm = Boolean(selectedKbId && selectedKb && !selectedKb.formName);

  function ChatPanel({ title, description, children }) {
    return (
      <div className="today-panel chat-section-panel">
        <div className="today-panel-header">
          <span className="today-panel-title">{title}</span>
        </div>
        <div className="today-panel-body">
          {description && (
            <p className="chat-panel-desc">{description}</p>
          )}
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)', minWidth: 0 }}>
      <div className="today-page chat-page" style={{ padding: pagePad }}>
        <div className="today-header">
          <div className="today-header-left" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
            <h1 className="today-h1">Live Chat Widget</h1>
            <p className="chat-page-desc">
              Add AI-powered live chat to any website. Uses your knowledge base so visitors get instant, contextual answers.
            </p>
          </div>
        </div>

        {loading ? (
          <p style={{ color: 'var(--muted)', margin: 0 }}>Loading…</p>
        ) : !selectedWorkspaceId ? (
          <p style={{ color: 'var(--muted)', margin: 0 }}>Select a workspace to see your embed code.</p>
        ) : knowledgeBases.length === 0 ? (
          <div className="today-panel chat-section-panel">
            <div className="today-panel-header">
              <span className="today-panel-title">No knowledge base yet</span>
            </div>
            <div className="today-panel-body">
              <p className="chat-panel-desc">
                The chat widget uses a knowledge base that comes from your forms. Each form’s AI Form Reply settings create a knowledge base. Build a form first, set up its AI reply knowledge base, then return here to get your chat embed code.
              </p>
              <button type="button" className="inbox-pill-btn-primary" onClick={() => navigate('/forms/new')}>
                Create a form
              </button>
            </div>
          </div>
        ) : (
          <>
            {selectedKbMissingForm && (
              <div
                className="today-panel chat-section-panel"
                style={{
                  marginBottom: '1rem',
                  borderColor: 'color-mix(in srgb, var(--warn) 45%, transparent)',
                  background: 'color-mix(in srgb, var(--warn) 8%, transparent)',
                }}
              >
                <div className="today-panel-body" style={{ paddingTop: '12px', paddingBottom: '12px' }}>
                  <p className="chat-panel-desc" style={{ margin: 0, color: 'var(--text)' }}>
                    <strong>Inbox not connected.</strong> This knowledge base is not linked to a form yet, so &quot;Get in touch&quot; contacts will email you but will <em>not</em> appear in Leads or receive AI email follow-up. Link a form&apos;s AI reply settings to this knowledge base, then pick that form&apos;s KB here.
                  </p>
                </div>
              </div>
            )}
            <ChatPanel
              title="Saved chat configs"
              description="Save different setups for multiple websites or forms. Each config stores a label, knowledge base, and CTA."
            >
              <div className="chat-config-row">
                <select
                  className="fb-select chat-config-select"
                  value={selectedConfigId || '__new__'}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '__new__') clearForm();
                    else {
                      const c = savedConfigs.find((x) => x.id === v);
                      if (c) loadConfig(c);
                    }
                  }}
                >
                  <option value="__new__">New config</option>
                  {savedConfigs.map((c) => (
                    <option key={c.id} value={c.id}>{c.label || 'Unnamed'}</option>
                  ))}
                </select>
                <input
                  type="text"
                  className="fb-input chat-config-label-input"
                  placeholder="Label (e.g. Main site, Contact form)"
                  value={configLabel}
                  onChange={(e) => setConfigLabel(e.target.value)}
                />
                <button
                  type="button"
                  className="inbox-pill-btn-primary"
                  onClick={saveConfig}
                  disabled={saving || !selectedKbId}
                >
                  {saving ? 'Saving…' : 'Save config'}
                </button>
                {selectedConfigId && (
                  <button
                    type="button"
                    className="inbox-pill-btn chat-delete-btn"
                    onClick={() => deleteConfig(selectedConfigId)}
                  >
                    Delete
                  </button>
                )}
              </div>
            </ChatPanel>

            <ChatPanel
              title="Knowledge base for chat"
              description="Each knowledge base is tied to a form. The form name is shown so you know which form this chat uses. That same form name is used as the reply identity in AI email replies."
            >
              <select
                className="fb-select chat-kb-select"
                value={selectedKbId}
                onChange={(e) => setSelectedKbId(e.target.value)}
              >
                {knowledgeBases.map((kb) => (
                  <option key={kb.id} value={kb.id}>
                    {kb.displayLabel}
                  </option>
                ))}
              </select>
            </ChatPanel>

            <ChatPanel
              title="Chat content (optional)"
              description="Add chat-specific content here for a warmer, more conversational tone. When empty, chat uses the main KB (same as email). Use this to write content that builds excitement and naturally leads visitors to contact you – without the strict rules needed for the shared KB."
            >
              <textarea
                className="fb-textarea chat-content-input"
                value={chatContent}
                onChange={(e) => setChatContent(e.target.value)}
                placeholder="e.g. We help with steel buildings of all sizes. Coastal areas need special engineering – we handle that. Happy to put together a quote. Use the links below to schedule or reach us."
                rows={5}
              />
              <button
                type="button"
                className="inbox-pill-btn-primary"
                onClick={saveChatContent}
                disabled={savingChatContent || !selectedKbId}
                style={{ marginTop: '0.75rem' }}
              >
                {savingChatContent ? 'Saving…' : 'Save chat content'}
              </button>
            </ChatPanel>

            <ChatPanel
              title="Call to action (optional)"
              description="Add a calendar link, phone, or email. These appear in the chat and the AI can direct visitors here when they want to schedule or be contacted."
            >
              <div className="chat-cta-fields">
                <input
                  type="url"
                  className="fb-input"
                  placeholder="Calendar URL (e.g. https://calendly.com/yourlink)"
                  value={ctaUrl}
                  onChange={(e) => setCtaUrl(e.target.value)}
                />
                <input
                  type="text"
                  className="fb-input"
                  placeholder="Calendar link text (e.g. Schedule a call)"
                  value={ctaText}
                  onChange={(e) => setCtaText(e.target.value)}
                />
                <input
                  type="tel"
                  className="fb-input"
                  placeholder="Phone number"
                  value={ctaPhone}
                  onChange={(e) => setCtaPhone(e.target.value)}
                />
                <input
                  type="email"
                  className="fb-input"
                  placeholder="Email"
                  value={ctaEmail}
                  onChange={(e) => setCtaEmail(e.target.value)}
                />
              </div>
            </ChatPanel>

            <ChatPanel
              title="Conversion tracking (optional)"
              description="Add tracking for this chat. When empty, uses the form's tracking (from the form that owns this KB)."
            >
              <TrackingConfig
                formId={selectedConfigId || 'chat-config'}
                chatConfigId={selectedConfigId || undefined}
                formName={configLabel || 'Chat'}
                trackingPixels={configTrackingPixels}
                onSave={async (updatedPixels) => {
                  setConfigTrackingPixels(updatedPixels);
                  if (selectedConfigId) {
                    try {
                      await updateDoc(doc(db, 'chat_configs', selectedConfigId), { trackingPixels: updatedPixels, updatedAt: new Date() });
                    } catch (e) {
                      console.error('Error saving chat tracking:', e);
                    }
                  }
                }}
              />
            </ChatPanel>

            <ChatPanel
              title="Widget appearance"
              description="Accent color for the chat launcher, header, and your message bubbles. Defaults to gocta lime; leave default to omit from embed code."
            >
              <p className="chat-panel-desc" style={{ marginTop: 0 }}>Theme</p>
              <div className="fb-segmented" style={{ marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className={`fb-segmented-btn${themeMode === 'light' ? ' active' : ''}`}
                  onClick={() => setThemeMode('light')}
                >
                  Light (default)
                </button>
                <button
                  type="button"
                  className={`fb-segmented-btn${themeMode === 'dark' ? ' active' : ''}`}
                  onClick={() => setThemeMode('dark')}
                >
                  Dark
                </button>
                <button
                  type="button"
                  className={`fb-segmented-btn${themeMode === 'auto' ? ' active' : ''}`}
                  onClick={() => setThemeMode('auto')}
                >
                  Auto
                </button>
              </div>
              <p className="chat-panel-desc" style={{ marginTop: 0, marginBottom: '1rem' }}>
                Light matches your site&apos;s branding on any background. Auto follows each visitor&apos;s device theme (can look different per visitor).
              </p>
              <div className="chat-accent-row">
                <input
                  type="color"
                  className="chat-accent-swatch"
                  value={accentColor}
                  onChange={(e) => setAccentColor(normalizeHex(e.target.value))}
                  aria-label="Accent color"
                />
                <input
                  type="text"
                  className="fb-input chat-accent-hex"
                  value={accentColor}
                  onChange={(e) => setAccentColor(normalizeHex(e.target.value))}
                  placeholder="#C8FF4D"
                  spellCheck={false}
                />
              </div>
            </ChatPanel>

            <ChatPanel
              title="Embed code"
              description={<>Copy this before <code className="fb-code">&lt;/body&gt;</code>. CTAs (<code className="fb-code">data-cta-url</code>, etc.) appear as contextual in-thread chips — not a footer link bar.</>}
            >
              <pre className="fb-dev-pre chat-embed-pre">{embedCode}</pre>
              <button
                type="button"
                className="inbox-pill-btn"
                onClick={() => navigator.clipboard?.writeText(embedCode)}
                style={{ marginTop: '0.75rem' }}
              >
                Copy code
              </button>
            </ChatPanel>

            <ChatPanel
              title="Try it here"
              description="Load the chat widget on this page to test it. The chat button will appear in the bottom-right corner."
            >
              <button
                type="button"
                className="inbox-pill-btn-primary"
                onClick={() => setTryIt(true)}
                disabled={tryIt}
              >
                {tryIt ? 'Chat loaded – see bottom-right' : 'Load chat widget'}
              </button>
            </ChatPanel>
          </>
        )}
      </div>
    </div>
  );
}
